import {
  bookIdentity,
  accountIds,
  confirmExitAllOf,
  cancelMessage,
  checkIntent,
  checkStopMove,
  positionKey,
  protectiveStopWords,
  type OrderEvent,
  type StopMoveCheck,
  confirmLargeOrdersOf,
  confirmNewOrder,
  exitMessage,
  forceExitMessage,
  gateAction,
  resyncMessage,
  modifyMessage,
  largeQtyWords,
  newRequestId,
  placeOrderMessage,
  useConnection,
  useSettings,
  useTrading,
  type CommandOutcome,
  type ExitActionKind,
  type Gate,
  type GateAction,
  type OrderIntent,
} from "@helm/core";
import { pushToast } from "../components/Toasts";
import { commands, gateContextOf } from "./control";
import { settingsSync } from "../session";
import { marketData } from "../md/marketData";
import {
  confirmOrder,
  confirmOrderChoice,
  isFlattenAllOpen,
  openFlattenAll,
  watchExit,
  type Anchor,
} from "./hosts";

/**
 * Every trading action the UI can take, each through the trading socket's existing commands
 * (`PlaceOrder`, `ModifyOrder`, `CancelOrder`, `ExitAction`) — never a second submit path.
 *
 * Each call re-checks the gate at the moment of the action (a hotkey or a stale render must not
 * slip past a disabled button), sends once, and reports the gateway's `CommandResult` verbatim.
 * Nothing is retried and nothing is shown as working until the gateway's `OrderEvent` says so.
 */

/** Gate check at action time, from the live stores (not a render's snapshot). */
export function gateNow(action: GateAction): Gate {
  const ctx = gateContextOf(
    useConnection.getState().status === "connected",
    useTrading.getState(),
    Date.now(),
  );
  return gateAction(ctx, action);
}

function refuse(title: string, g: Gate): false {
  if (!g.ok) pushToast({ tone: "warn", title, body: g.reason }, { ttlMs: 5000 });
  return false;
}

function report(title: string, r: CommandOutcome | null): boolean {
  if (r === null) return false; // suppressed double click: nothing was sent
  if (!r.ok) {
    pushToast(
      { tone: "danger", title: `${title} — ${r.code}`, body: r.message || "Refused by the gateway" },
      { assertive: true, ttlMs: 9000 },
    );
    return false;
  }
  return true;
}

/**
 * Place an order. With one-click trading off (SPEC §9.8 default) the order is confirmed in a
 * popover at the click first; `words` is what the user confirms. A large quantity (above the
 * account's threshold, `orderConfirmation`) is confirmed whatever the one-click setting, in words
 * that name the quantity, side, contract and account — until the user ticks "Don't ask again for
 * large orders" and confirms (saved in the synced settings, `confirm_large_orders`; Settings →
 * "Confirm large orders" turns it back on).
 */
export async function placeOrder(
  key: string,
  intent: OrderIntent,
  gate: GateAction,
  words: string,
  anchor: Anchor | null,
): Promise<boolean> {
  const g = gateNow(gate);
  if (!g.ok) return refuse(words, g);
  const bad = checkIntent(intent);
  if (bad !== null) {
    pushToast({ tone: "warn", title: words, body: bad }, { ttlMs: 5000 });
    return false;
  }
  if (commands.isBusy(key)) return false;
  // SPEC §9.8 / OB4: a large quantity is confirmed even with one-click on, unless turned off.
  const settings = useSettings.getState().settings;
  const c = await confirmNewOrder({
    oneClick: settings.oneClick,
    confirmLarge: confirmLargeOrdersOf(settings.confirm_large_orders),
    qty: intent.qty,
    risk: useTrading.getState().risk.get(intent.accountId),
    words,
    largeWords: (threshold) => largeQtyWords(intent, threshold),
    ask: (req) => confirmOrderChoice({ ...req, anchor }),
    save: (patch) => {
      void settingsSync.update(patch);
    },
  });
  if (c.asked) {
    if (!c.ok) return false;
    const again = gateNow(gate);
    if (!again.ok) return refuse(words, again);
  }
  // A fresh idempotency key per action; the gateway refuses a repeat for 24 h.
  const r = await commands.run(key, { case: "place", value: placeOrderMessage(intent, newRequestId()) });
  const ok = report(words, r);
  if (ok)
    pushToast(
      { tone: "info", title: "Order sent", body: `${words} — waiting for the exchange` },
      { ttlMs: 2500 },
    );
  return ok;
}

export async function cancelOrder(
  key: string,
  accountId: string,
  orderId: string,
  label: string,
): Promise<boolean> {
  const g = gateNow({ kind: "cancel", accountId });
  if (!g.ok) return refuse(label, g);
  return report(label, await commands.run(key, { case: "cancel", value: cancelMessage(orderId) }));
}

/** Moving working order `o`'s stop to `stopTicks`, against the live position and market (AB-3). */
export function stopMoveCheckOf(o: OrderEvent, stopTicks: number | undefined): StopMoveCheck {
  const net = useTrading.getState().positions.get(positionKey(o.accountId, o.symbol))?.netQty ?? 0;
  const m = marketData.findByContract(o.symbol) ?? marketData.get(o.symbol);
  return checkStopMove(o, stopTicks, net, {
    bid: m?.bidTicks ?? null,
    ask: m?.askTicks ?? null,
    last: m?.lastTicks ?? null,
  });
}

/**
 * Before moving working order `o`'s stop to `stopTicks` (DOM or chart drag, AB-3): a protective
 * stop at or through the market triggers at once, so that move is confirmed
 * first, whatever the one-click setting. Resolves true when the move may be sent: nothing to ask,
 * the user confirmed, or an entry stop (the gateway refuses those with `STOP_WRONG_SIDE`).
 */
export async function confirmStopMove(
  o: OrderEvent,
  stopTicks: number | undefined,
  price: (ticks: number) => string,
  anchor: Anchor | null,
): Promise<boolean> {
  const check = stopMoveCheckOf(o, stopTicks);
  if (check.kind !== "protective") return true;
  const choice = await confirmOrderChoice({
    words: protectiveStopWords(check, o.symbol, price),
    anchor,
    warning: {
      title: "Stop triggers now",
      confirmLabel: check.stopLimit ? "Move & trigger" : "Move & exit",
      note: check.stopLimit
        ? "Moving this stop triggers its limit order. It may remain unfilled and leave the position open. Cancel keeps it where it is."
        : "The stop is through the market: moving it sends a market exit. Cancel keeps it where it is.",
    },
  });
  return choice.ok;
}

export async function modifyOrder(
  key: string,
  accountId: string,
  orderId: string,
  change: { limitTicks?: number; stopTicks?: number; qty?: number },
  label: string,
): Promise<boolean> {
  const g = gateNow({ kind: "modify", accountId });
  if (!g.ok) return refuse(label, g);
  return report(label, await commands.run(key, { case: "modify", value: modifyMessage(orderId, change) }));
}

const EXIT_GATE: Readonly<Partial<Record<ExitActionKind, "exitSymbol" | "cancelAll" | "flattenAccount">>> = {
  EXIT_AND_CANCEL_SYMBOL: "exitSymbol",
  CANCEL_ALL_ACCOUNT: "cancelAll",
  // Cancels only (DOM "Cxl" per side / contract): never gated beyond the session and account.
  CANCEL_SYMBOL_ORDERS: "cancelAll",
  CANCEL_BIDS: "cancelAll",
  CANCEL_OFFERS: "cancelAll",
  FLATTEN_ACCOUNT: "flattenAccount",
};

/**
 * An account-level §9.7 action: Exit & Cxl (symbol), Cxl All, Flatten (account). Exits that
 * close positions are followed until the gateway reports the scope flat (`watchExit`).
 */
export async function exitAction(
  key: string,
  kind:
    | "EXIT_AND_CANCEL_SYMBOL"
    | "CANCEL_ALL_ACCOUNT"
    | "CANCEL_SYMBOL_ORDERS"
    | "CANCEL_BIDS"
    | "CANCEL_OFFERS"
    | "FLATTEN_ACCOUNT",
  accountId: string | null,
  symbol: string | null,
  label: string,
): Promise<boolean> {
  const gk = EXIT_GATE[kind] ?? "flattenAccount";
  const g =
    gk === "exitSymbol"
      ? gateNow({ kind: "exitSymbol", accountId, contract: symbol })
      : gateNow({ kind: gk, accountId });
  if (!g.ok || accountId === null) return refuse(label, g);
  const identity = bookIdentity(useTrading.getState());
  const r = await commands.run(key, {
    case: "exitAction",
    value: exitMessage(kind, accountId, symbol ?? ""),
  });
  const ok = report(label, r);
  const cancelOnly = kind !== "EXIT_AND_CANCEL_SYMBOL" && kind !== "FLATTEN_ACCOUNT";
  if (ok && !cancelOnly) {
    watchExit(
      { accounts: [accountId], symbol: kind === "EXIT_AND_CANCEL_SYMBOL" ? symbol : null },
      label,
      identity,
    );
  }
  if (ok && cancelOnly) {
    pushToast({ tone: "info", title: label, body: "Cancel requests sent" }, { ttlMs: 2500 });
  }
  return ok;
}

/**
 * Reverse & Cxl (SPEC §9.7): the gateway cancels the contract's orders, waits for every cancel,
 * re-reads the confirmed position N and sends one market order of 2×|N| the other way. It opens
 * a new position, so with one-click off it is confirmed like an order. One fresh request id per
 * click: the gateway refuses a repeat, so a resent frame can never reverse twice.
 */
export async function reverseAction(
  key: string,
  accountId: string | null,
  contract: string | null,
  netQty: number,
  unknown: { buy: boolean; sell: boolean },
  anchor: Anchor | null,
  valid?: (() => boolean) & { dispose?: () => void; accountLabel?: string },
): Promise<boolean> {
  try {
    const label = `Reverse & Cxl ${contract ?? ""}`.trim();
    const gate: GateAction = { kind: "reverse", accountId, contract, netQty, unknownSides: unknown };
    const g = gateNow(gate);
    if (!g.ok || accountId === null || contract === null) return refuse(label, g);
    if (commands.isBusy(key)) return false;
    const n = Math.abs(netQty);
    const target = valid?.accountLabel === undefined ? label : `${label} · account ${valid.accountLabel}`;
    const words = `${target}: cancel ${contract} orders, then ${netQty > 0 ? "Sell" : "Buy"} ${String(2 * n)} at market → ${netQty > 0 ? "−" : "+"}${String(n)}`;
    if (!useSettings.getState().settings.oneClick) {
      const yes = await confirmOrder({ words, anchor });
      if (!yes) return false;
      const again = gateNow(gate);
      if (!again.ok) return refuse(label, again);
    }
    // Optional chart-chip intent check; other entry surfaces keep their existing behavior.
    if (valid && !valid()) return false;
    const r = await commands.run(key, {
      case: "exitAction",
      value: exitMessage("REVERSE_AND_CANCEL_SYMBOL", accountId, contract, false, newRequestId()),
    });
    const ok = report(label, r);
    if (ok)
      pushToast({ tone: "info", title: label, body: "Cancelling orders, then reversing" }, { ttlMs: 2500 });
    return ok;
  } finally {
    valid?.dispose?.();
  }
}

/** Exit All follows its own remembered confirmation preference (owner 2026-10-02). */
export function requestFlattenAll(): void {
  // A changed preference cannot create a second actionable intent beside an open dialog.
  if (isFlattenAllOpen() || commands.isBusy("flatten-all")) return;
  const g = gateNow({ kind: "flattenAll" });
  if (!g.ok) {
    refuse("Exit All — all accounts", g);
    return;
  }
  if (confirmExitAllOf(useSettings.getState().settings.confirm_exit_all)) openFlattenAll();
  else void flattenAllConfirmed(accountIds(useTrading.getState()));
}

/** Same submit path for explicit confirmation or a remembered Exit All preference. */
export async function flattenAllConfirmed(
  accounts: readonly string[],
  dontAskAgain = false,
): Promise<boolean> {
  const label = "Exit All — all accounts";
  const g = gateNow({ kind: "flattenAll" });
  if (!g.ok) return refuse(label, g);
  if (commands.isBusy("flatten-all")) return false;
  // Record only an accepted Confirm click; persistence must never delay the exit request.
  if (dontAskAgain) void settingsSync.update({ confirm_exit_all: false });
  const identity = bookIdentity(useTrading.getState());
  const r = await commands.run("flatten-all", {
    case: "exitAction",
    value: exitMessage("FLATTEN_ALL_ACCOUNTS", "", "", true),
  });
  const ok = report(label, r);
  if (ok) watchExit({ accounts, symbol: null }, label, identity);
  return ok;
}

/**
 * "Force exit anyway" for an exit the gateway holds back because a protective order's cancel is
 * unconfirmed (ADR-0050), sent only from the banner's confirmation dialog. The gateway re-checks
 * everything: only past its wait, only closing, never while an order on that side is UNKNOWN.
 */
/**
 * "Resync with Rithmic" (ADR-0059), after the menu's confirmation: one `Resync` command. The
 * gateway answers at once that it started; what it found arrives as the `RESYNC_DONE` notice
 * ("Resynced with Rithmic: 0 working order(s), positions flat; …"), shown as its own toast.
 */
export async function resyncWithRithmic(loginId = ""): Promise<boolean> {
  const label = "Resync with Rithmic";
  const r = await commands.run(`resync:${loginId}`, { case: "resync", value: resyncMessage(loginId) });
  const ok = report(label, r);
  if (ok) {
    pushToast(
      {
        tone: "info",
        title: label,
        body: "Re-reading working orders, order history and positions from Rithmic…",
      },
      { ttlMs: 5000 },
    );
  }
  return ok;
}

export async function forceExit(accountId: string): Promise<boolean> {
  const label = `Force exit ${accountId}`;
  const loginId = useTrading.getState().accounts.get(accountId)?.loginId ?? "";
  const r = await commands.run(`force-exit:${accountId}`, {
    case: "forceExit",
    value: forceExitMessage(accountId, "", loginId),
  });
  const ok = report(label, r);
  if (ok) {
    pushToast(
      {
        tone: "warn",
        title: label,
        body: "Forced exit sent — check the position and the orders in R|Trader now",
      },
      { ttlMs: 8000 },
    );
  }
  return ok;
}
